import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Loader from 'react-spinners/PulseLoader';

import { RoutePaths } from 'router/Routes';
import AuthLayout from 'components/layout/AuthLayout';
import MainLayout from 'components/layout/MainLayout';
import GeneralLayout from 'components/layout/GeneralLayout';
import { useIsSessionOpen } from 'hooks/useIsSessionOpen';

// Lazy load pages
const Dashboard = lazy(() => import('components/pages/Dashboard'));
const Estudiantes = lazy(() => import('components/pages/Estudiantes'));
const Users = lazy(() => import('components/pages/Users'));
const Program = lazy(() => import('components/pages/Program'));
const Settings = lazy(() => import('components/pages/Settings'));
const Congregations = lazy(() => import('components/pages/Congregations'));
const MainPage = lazy(() => import('components/pages/MainPage'));
const Page404 = lazy(() => import('components/pages/Page404'));
const LoginForm = lazy(() => import('components/forms/LoginForm'));
const SelectCongregation = lazy(() => import('components/forms/SelectCongregation'));

// Loader component
const PageLoader = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loader color="#4a6da7" size={10} />
    </div>
);

// Protected Route wrapper component
function ProtectedRoute({ children }: { children: JSX.Element }) {
    const isSessionOpen = useIsSessionOpen();

    if (!isSessionOpen) {
        return <Navigate to={RoutePaths.LOGIN} replace />;
    }

    return children;
}

export function AppRouter() {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                {/* Auth Routes */}
                <Route path={RoutePaths.LOGIN} element={<AuthLayout />}>
                    <Route index element={<LoginForm />} />
                </Route>
                <Route path={RoutePaths.SELECT_CONGREGATION} element={<AuthLayout />}>
                    <Route index element={<SelectCongregation />} />
                </Route>

                {/* Protected Routes */}
                <Route
                    path={RoutePaths.DASHBOARD}
                    element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Dashboard />} />
                </Route>
                <Route
                    path={RoutePaths.ESTUDIANTES}
                    element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Estudiantes />} />
                </Route>
                <Route
                    path={RoutePaths.USUARIOS}
                    element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Users />} />
                </Route>
                <Route
                    path={RoutePaths.CONFIGURACION}
                    element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Settings />} />
                </Route>
                <Route
                    path={RoutePaths.CONGREGACIONES}
                    element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Congregations />} />
                </Route>
                <Route
                    path={RoutePaths.PROGRAMA}
                    element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Program />} />
                </Route>

                {/* Public Routes */}
                <Route path={RoutePaths.HOME} element={<GeneralLayout />}>
                    <Route index element={<MainPage />} />
                </Route>

                {/* 404 Route */}
                <Route path="*" element={<GeneralLayout />}>
                    <Route index element={<Page404 />} />
                </Route>
            </Routes>
        </Suspense>
    );
}
